import styled from '@emotion/styled';
import { Button, colors, Text } from '@innovationdepartment/proxima-ui';
import ModalBase, { ModalBaseChildrenWrapper } from 'ui/ModalBase';
import {
  type AdSetAtRisk,
  adSetsAtRisk,
  type DeletePreviewAccount,
  groupDeletePreview
} from '../lib/audienceDelete';
import { formatMetaStatus } from '../lib/formatters';
import type { DeletePreviewState } from '../store/customAudiencesSlice';
import { Notice } from './form.styles';
import { TreeGlyph } from './shared.styles';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 640px;
  max-width: 100%;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const PlainList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const AccountList = styled(PlainList)`
  gap: 12px;
`;

const AudienceList = styled(PlainList)`
  gap: 4px;
  margin-top: 4px;
  font-size: 13px;
  color: ${colors.neutral90};
`;

const AudienceItem = styled.li<{ nested: boolean }>`
  padding-left: ${({ nested }) => (nested ? '16px' : '0')};
`;

const Muted = styled.span`
  color: ${colors.neutral60};
`;

const RetryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

type DeleteAudienceModalProps = {
  open: boolean;
  audienceName: string;
  /** The preview for this definition, or null before it starts loading. */
  preview: DeletePreviewState | null;
  /** True while the delete request runs, when the dialog cannot be closed. */
  deleting: boolean;
  onRetryPreview: () => void;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Confirms a definition delete against a preview of what it removes on Meta.
 *
 * - The audiences to be deleted are listed by ad account, each seed ahead of its lookalikes.
 * - Ad sets using any of them are listed under a warning, and the confirm reads "Delete anyway".
 * - A preview that is loading, failed, or carries a `blockedReason` disables the confirm.
 */
const DeleteAudienceModal = (props: DeleteAudienceModalProps) => {
  const { preview, deleting } = props;
  const loaded = preview?.status === 'loaded' ? preview.preview : null;
  const accounts = loaded ? groupDeletePreview(loaded) : [];
  const adSets = adSetsAtRisk(accounts);
  const canConfirm = loaded != null && loaded.blockedReason == null && !deleting;
  let confirmLabel = adSets.length > 0 ? 'Delete anyway' : 'Delete';
  if (deleting) confirmLabel = 'Deleting…';

  return (
    <ModalBase
      open={props.open}
      title="Delete custom audience"
      onClose={deleting ? undefined : props.onClose}
    >
      <ModalBaseChildrenWrapper
        submitButton={
          <ActionButtons>
            <Button
              variant="outlined"
              color="secondary"
              label="Cancel"
              disabled={deleting}
              onClick={props.onClose}
            />
            <Button
              size="small"
              color="error"
              label={confirmLabel}
              disabled={!canConfirm}
              onClick={props.onConfirm}
            />
          </ActionButtons>
        }
      >
        <ModalContent>
          <Text variant="body1" color="neutral100">
            Delete {props.audienceName}? Its audiences on Meta are deleted too. This can't be
            undone.
          </Text>
          {preview == null || preview.status === 'loading' ? (
            <Text variant="body2" color="neutral60">
              Checking Meta for ad sets that use this audience…
            </Text>
          ) : null}
          {preview?.status === 'error' ? (
            <Notice tone="error" role="alert">
              <RetryRow>
                <span>Couldn't check what this delete removes: {preview.message}</span>
                <Button
                  label="Retry"
                  variant="outlined"
                  size="small"
                  onClick={props.onRetryPreview}
                />
              </RetryRow>
            </Notice>
          ) : null}
          {loaded?.blockedReason ? (
            <Notice tone="error" role="alert">
              {loaded.blockedReason}
            </Notice>
          ) : null}
          {adSets.length > 0 ? <AdSetsWarning adSets={adSets} /> : null}
          {loaded ? <AudiencesToDelete accounts={accounts} /> : null}
        </ModalContent>
      </ModalBaseChildrenWrapper>
    </ModalBase>
  );
};

/* The ad sets that stop delivering once their audiences are gone */
const AdSetsWarning = ({ adSets }: { adSets: AdSetAtRisk[] }) => (
  <Notice tone="warning" role="alert">
    {adSets.length === 1 ? '1 ad set uses' : `${adSets.length} ad sets use`} these audiences. Ads
    using them stop running and can't be restarted.
    <ul aria-label="Ad sets using these audiences">
      {adSets.map(({ adSet, audiences }) => (
        <li key={adSet.id}>
          {adSet.name ?? adSet.id} · {formatMetaStatus(adSet.effectiveStatus)}
          {adSet.campaignName ? ` · ${adSet.campaignName}` : ''} · uses {audiences.join(', ')}
        </li>
      ))}
    </ul>
  </Notice>
);

/* Every audience the delete removes, by ad account */
const AudiencesToDelete = ({ accounts }: { accounts: DeletePreviewAccount[] }) => {
  if (accounts.length === 0)
    return (
      <Text variant="body2" color="neutral60">
        This audience has no destinations, so nothing is removed on Meta.
      </Text>
    );
  const unchecked = accounts.flatMap(account => account.audiences).filter(row => row.adSetsUnknown);
  return (
    <section aria-label="Audiences to delete">
      <AccountList>
        {accounts.map(account => (
          <li key={account.adAccountId}>
            <Text variant="body2Semibold" color="neutral100">
              {account.adAccountName ?? account.adAccountId}{' '}
              <Muted>
                {account.adAccountId}
                {account.isProximaOwned ? ' · Proxima' : ''}
              </Muted>
            </Text>
            <AudienceList>
              {account.audiences.map(audience => (
                <AudienceItem key={audience.key} nested={audience.nested}>
                  {audience.nested ? <TreeGlyph aria-hidden>└ </TreeGlyph> : null}
                  {audience.label}
                  {audience.name ? ` · ${audience.name}` : ''}
                  {audience.tracked ? null : <Muted> · not created by Proxima</Muted>}
                  {audience.pending ? <Muted> · not on Meta yet</Muted> : null}
                  {audience.adSetsUnknown ? (
                    <Muted>
                      {' '}
                      · ad sets not checked{audience.adSetsError ? `: ${audience.adSetsError}` : ''}
                    </Muted>
                  ) : null}
                </AudienceItem>
              ))}
            </AudienceList>
          </li>
        ))}
      </AccountList>
      {unchecked.length > 0 ? (
        <Text variant="body3" color="neutral60">
          Meta couldn't be checked for{' '}
          {unchecked.length === 1 ? '1 audience' : `${unchecked.length} audiences`}, so ads may be
          using {unchecked.length === 1 ? 'it' : 'them'} without being listed.
        </Text>
      ) : null}
    </section>
  );
};

export default DeleteAudienceModal;
